"use client";

import React, { useEffect, useState, useCallback } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import { Matchcentre } from "@nrldigitaltechnology/ui-community";
import { useBreadcrumb } from "@nrldigitaltechnology/ui-community";
import {
  GET_REGIONS,
  GET_STATES,
} from "@/backend-graphql/queries/backend-graph-queries";
import { CommonProps } from "@nrldigitaltechnology/ui-community/dist/components/Matchcentre/types";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { defaultLocations, defaultTfaLocations, prlProdDefaultLocations, tfaProdDefaultLocations } from "@/utils/constants";
import { LoadingSpinner } from "@/utils/components/LoadingSpinner";
import { getNationalId } from "@/utils/utilities";
import { useStateRegionStore } from "@/store/store";

export const MainClient = () => {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const type = searchParams.get("type");
  const stateId = params?.id;

  const globalNationalId = getNationalId();

  const { state, setState, setRegion, clearState, clearRegion } = useStateRegionStore();
  const { captureNav, setCrumbs } = useBreadcrumb();

  const [states, setStates] = useState<{ stateOptions: any[] }>({
    stateOptions: [],
  });
  const [regions, setRegions] = useState({
    regionOptions: [],
  });
  const { data: statesData, loading: stateLoading } = useQuery(GET_STATES, {
    variables: { nationalId: globalNationalId.toString() },
  });

  const [fetchRegionsQuery, { data: regionsData, loading: regionLoading }] = useLazyQuery(GET_REGIONS, {
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (!stateId) {

      if (state?.value) {
        clearState();
      }
      if (regions?.regionOptions?.length > 0) {
        clearRegion();
        setRegions({ regionOptions: [] });
      }
      setCrumbs([{ id: "/competitions", label: "Home" }]);
    } else {
      const label = searchParams.get('label');


      if (label && states?.stateOptions?.length > 0) {
        const matchingState = states.stateOptions.find(
          (s: CommonProps) => s.value === stateId
        );
        if (matchingState && (!state?.value || state.value !== stateId)) {
          setState(matchingState);
        }
      }

      fetchRegionsQuery({
        variables: {
          stateId: Number(stateId),
          nationalId: globalNationalId,
        },
      });

      // Restore breadcrumb from URL on page load/refresh/back
      if (label) {
        const path = `/competitions/state/${stateId}`;
        setCrumbs([
          { id: "/competitions", label: "Home" },
          { id: path, label: label }
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId, searchParams]);

  useEffect(() => {
    if (statesData?.states.length > 0) {
      const options = statesData?.states?.map((state: any) => ({
        label: state.name,
        value: state._id,
        logo: state?.meta?.avatar || "/assets/logo/noimage.png",
      }));

      if (process.env.NEXT_PUBLIC_APP_ENV === "prod") {
        const prodSelectedOptions =
          globalNationalId === 31 ? tfaProdDefaultLocations : prlProdDefaultLocations;

        const prodOrderedOptions: any[] = [];

        for (let i = 0; i < options.length; i++) {
          let present = false;
          for (let j = 0; j < prodSelectedOptions.length; j++) {
            if (options[j].value === prodSelectedOptions[i]) {
              present = true;
              prodOrderedOptions.push(options[j]);
            }
          }
          if (!present) {
            prodOrderedOptions.push(options[i]);
          }
        }
        setStates((prev) => ({ ...prev, stateOptions: prodOrderedOptions }));

      } else {
        const selectedOptions =
          globalNationalId === 31 ? defaultTfaLocations : defaultLocations;

        let filteredOptions: any = null;

        filteredOptions = options.filter(
          (fitem: { label: string; value: string; logo: string }) =>
            selectedOptions.some((som) => som === Number(fitem.value))
        );
        const orderedOptions: any = [];
        for (let i = 0; i < selectedOptions.length; i++) {
          for (let j = 0; j < filteredOptions.length; j++) {
            if (filteredOptions[j].value === selectedOptions[i]) {
              orderedOptions.push(filteredOptions[j]);
              break;
            }
          }
        }
        setStates((prev) => ({ ...prev, stateOptions: orderedOptions }));
      }
    }
  }, [statesData]);

  useEffect(() => {
    if (states?.stateOptions.length > 0 && !stateId) {
      const firstState = states.stateOptions[0] as CommonProps;
      if (firstState?.value) {
        setState(firstState);

        (async () => {
          const clickedStateId = Number(firstState.value);
          setRegion({} as CommonProps);
          try {
            const result = await fetchRegionsQuery({
              variables: {
                stateId: clickedStateId,
                nationalId: globalNationalId,
              },
            });
            if (result.data || result.error) {
              const path = `/competitions/state/${firstState.value}`;
              setCrumbs([
                { id: "/competitions", label: "Home" },
                { id: path, label: firstState.label }
              ]);
            }
          } catch (error) {
            console.error('Error fetching regions:', error);
          }
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states?.stateOptions, stateId]);

  useEffect(() => {
    if (regionsData?.regions?.length > 0) {
      const options = regionsData?.regions.map((region: any) => ({
        label: region.name,
        value: region._id,
        state: region?.orgtree?.state?.name,
        logo: region?.meta?.avatar || "/assets/logo/noimage.png",
      }));
      setRegions((prev) => ({ ...prev, regionOptions: options }));
    }
  }, [regionsData]);


  const onClickLocation = useCallback(async (val: CommonProps) => {
    const clickedStateId = Number(val.value);
    setRegion({} as CommonProps);
    setState(val);
    const path = `/competitions/state/${val.value}`;
    setCrumbs([
      { id: "/competitions", label: "Home" },
      { id: path, label: val.label }
    ]);
    try {
      await fetchRegionsQuery({
        variables: {
          stateId: clickedStateId,
          nationalId: globalNationalId,
        },
      });
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  }, [setState, setRegion, fetchRegionsQuery, globalNationalId, setCrumbs]);

  const onClickRegions = useCallback((val: CommonProps) => {
    setRegion(val);

    const path = `/competitions/region/${val.value}`;
    const queryString = `?label=${encodeURIComponent(
      val.label
    )}&type=regional&locationId=${state?.value}`;
    const fullPath = `${path}${queryString}`;
    captureNav({ id: fullPath, label: val.label });
    router.push(fullPath, { scroll: false });
  }, [setRegion, state?.value, captureNav, router]);

  return (
    <>
      {stateLoading ? (
        <div className="flex justify-center items-center">
          <LoadingSpinner text="Loading..." />
        </div>
      ) : (
        <div className=" flex justify-center items-center">
          <div 
            data-an-module="location-region-selector"
            className="flex flex-col sm:items-stretch w-full py-4"
          >
            <Matchcentre
              isLoading={regionLoading}
              selectedLocation={state}
              location={
                states?.stateOptions.length > 0
                  ? states?.stateOptions.slice(0, 12)
                  : [{ label: "", value: "", logo: "/assets/logo/nologo.png" }]
              }
              region={regions?.regionOptions}
              fetchRegions={onClickLocation}
              clickOnRegions={(val) => onClickRegions(val)}
              statePlaceHolder="Please select a state"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MainClient;
